/**
 * Boîte réponses — Kanban of incoming emails with AI-drafted replies.
 * Layout inspired by Capitalead's "Réponses aux devis" screen, adapted to
 * Norea's dossier context and vocabulary.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import {
  Inbox,
  Loader2,
  Plus,
  MoreVertical,
  Trash2,
  Palette,
  Pencil,
  RefreshCw,
  RotateCcw,
  ArchiveRestore,
  ExternalLink,
  Send,
  Sparkles,
  X,
  Mail,
  ChevronDown,
  ChevronUp,
  Save,
  Settings2,
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import AiInsight from "../components/AiInsight";
import Breadcrumb from "../components/Breadcrumb";
import { api } from "../lib/api";

// ------------- Utilities -------------
function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// ------------- Draggable mail card -------------
function MailCard({ mail, onClick, isDragging }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `mail-${mail.message_id}`,
    data: { type: "mail", mail },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 100 }
    : undefined;

  const draftReady = mail.draft_status === "drafted" && (mail.user_draft || mail.ai_draft);
  const isSent = mail.draft_status === "sent";
  const draftError = mail.draft_status === "error" || !mail.dossier_id;
  const noAddress = !mail.from_email;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        // Prevent click firing after drag
        if (isDragging) return;
        onClick?.();
      }}
      className={`bg-white rounded-lg border border-norea-border p-3 mb-2 cursor-grab active:cursor-grabbing hover:border-norea-primary/40 hover:shadow-sm transition-all ${isDragging ? "opacity-40" : ""}`}
      data-testid={`mail-card-${mail.message_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-norea-textMuted truncate">
          {mail.from_name || mail.from_email || "Expéditeur inconnu"}
        </div>
        <div className="text-[10px] text-norea-textMuted whitespace-nowrap">{timeAgo(mail.sent_at)}</div>
      </div>
      <div className="text-sm font-medium text-norea-text line-clamp-2 leading-snug mb-1.5">
        {mail.subject || "(sans objet)"}
      </div>
      {mail.resume_court && (
        <div className="text-[12px] text-norea-textMuted italic line-clamp-2 mb-2">
          {mail.resume_court}
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        {draftReady && !isSent && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
            <Sparkles className="w-2.5 h-2.5" />
            Brouillon IA prêt
          </span>
        )}
        {isSent && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
            <Send className="w-2.5 h-2.5" />
            Envoyée
          </span>
        )}
        {noAddress && (
          <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
            Adresse manquante
          </span>
        )}
        {draftError && !noAddress && mail.draft_status === "error" && (
          <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
            IA en échec
          </span>
        )}
        {mail.urgent && (
          <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
            Urgent
          </span>
        )}
        {mail.attachments?.length > 0 && (
          <span className="text-[10px] text-norea-textMuted">
            {mail.attachments.length} PJ
          </span>
        )}
      </div>
    </div>
  );
}

// ------------- Column -------------
function Column({ col, mails, onOpenMail, onRename, onDelete, activeDragId, allColumns }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${col.id}`, data: { type: "column", col } });
  const [editing, setEditing] = useState(false);
  const [tempName, setTempName] = useState(col.name);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => setTempName(col.name), [col.name]);

  const commit = () => {
    if (tempName.trim() && tempName !== col.name) onRename(col.id, tempName.trim());
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col w-[300px] min-w-[300px] bg-norea-bg rounded-lg border ${isOver ? "border-norea-primary bg-norea-primary/5" : "border-norea-border"} transition-colors`}
      data-testid={`reponses-col-${col.id}`}
    >
      <div className="px-3 py-2.5 flex items-center gap-2 border-b border-norea-border">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: col.color }} />
        {editing ? (
          <input
            autoFocus
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setTempName(col.name); setEditing(false); }
            }}
            className="flex-1 bg-white border border-norea-border rounded px-1.5 py-0.5 text-sm font-medium outline-none focus:border-norea-primary"
            data-testid={`col-rename-input-${col.id}`}
          />
        ) : (
          <button
            className="flex-1 text-left text-sm font-semibold text-norea-text truncate hover:text-norea-primary"
            onDoubleClick={() => setEditing(true)}
            title="Double-clic pour renommer"
            data-testid={`col-title-${col.id}`}
          >
            {col.name}
          </button>
        )}
        <span className="text-[11px] font-mono text-norea-textMuted">{mails.length}</span>
        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="p-1 text-norea-textMuted hover:text-norea-text rounded"
            data-testid={`col-menu-${col.id}`}
          >
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 bg-white border border-norea-border rounded-md shadow-lg z-50 min-w-[160px] py-1"
              onMouseLeave={() => setMenuOpen(false)}
            >
              <button
                onClick={() => { setEditing(true); setMenuOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs text-norea-text hover:bg-norea-surfaceHover flex items-center gap-2"
                data-testid={`col-action-rename-${col.id}`}
              >
                <Pencil className="w-3 h-3" /> Renommer
              </button>
              {allColumns.length > 1 && (
                <button
                  onClick={() => { setMenuOpen(false); onDelete(col.id, col.name); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                  data-testid={`col-action-delete-${col.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Supprimer
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 p-2 overflow-y-auto max-h-[calc(100vh-260px)]">
        {mails.length === 0 && (
          <div className="text-center text-[11px] text-norea-textMuted py-8 italic">Aucun mail</div>
        )}
        {mails.map((m) => (
          <MailCard
            key={m.message_id}
            mail={m}
            onClick={() => onOpenMail(m)}
            isDragging={activeDragId === `mail-${m.message_id}`}
          />
        ))}
      </div>
    </div>
  );
}

// ------------- Drawer for mail detail -------------
function MailDrawer({ mail, columns, signature, onClose, onSent, onMoved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [reorienting, setReorienting] = useState(false);
  const [reorientPrompt, setReorientPrompt] = useState("");
  const [showReorient, setShowReorient] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!mail) return;
    setLoading(true);
    api.get(`/reponses/mails/${encodeURIComponent(mail.message_id)}`)
      .then((r) => {
        setDetail(r.data);
        const m = r.data.mail;
        setDraft(m.user_draft || m.ai_draft || "");
      })
      .catch(() => toast.error("Impossible de charger le mail"))
      .finally(() => setLoading(false));
  }, [mail?.message_id]);

  const regenerate = async () => {
    setReorienting(true);
    try {
      const r = await api.post(`/reponses/mails/${encodeURIComponent(mail.message_id)}/draft`, {
        user_prompt: reorientPrompt,
      });
      if (r.data?.body) {
        setDraft(r.data.body);
        toast.success("Réponse régénérée");
        setShowReorient(false);
        setReorientPrompt("");
      } else if (r.data?.error) {
        toast.error(r.data.error);
      }
    } catch {
      toast.error("Erreur IA");
    } finally {
      setReorienting(false);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.post(`/reponses/mails/${encodeURIComponent(mail.message_id)}/save-draft`, { user_draft: draft });
      toast.success("Brouillon sauvegardé");
    } catch {
      toast.error("Sauvegarde impossible");
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    if (!draft.trim()) return toast.error("Corps du mail vide");
    setSending(true);
    try {
      const r = await api.post(`/reponses/mails/${encodeURIComponent(mail.message_id)}/send`, { body: draft });
      toast.success("Mail envoyé");
      onSent?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const openInGmail = () => {
    const q = encodeURIComponent(`rfc822msgid:${mail.message_id}`);
    window.open(`https://mail.google.com/mail/u/0/#search/${q}`, "_blank");
  };

  const moveToColumn = async (colId) => {
    try {
      await api.patch(`/reponses/mails/${encodeURIComponent(mail.message_id)}/move`, { column_id: colId });
      onMoved?.(mail.message_id, colId);
    } catch {
      toast.error("Déplacement impossible");
    }
  };

  if (!mail) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="mail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-[720px] bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-norea-border px-6 py-4 flex items-center gap-3">
          <Mail className="w-5 h-5 text-norea-primary" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-norea-textMuted">
              De · {detail?.mail?.from_name || mail.from_name || mail.from_email}
            </div>
            <div className="text-base font-semibold text-norea-text truncate">
              {detail?.mail?.subject || mail.subject || "(sans objet)"}
            </div>
          </div>
          <button
            onClick={openInGmail}
            className="text-xs px-2.5 py-1.5 rounded border border-norea-border hover:bg-norea-surfaceHover flex items-center gap-1.5"
            data-testid="btn-open-gmail"
          >
            <ExternalLink className="w-3 h-3" />
            Ouvrir dans Gmail
          </button>
          <button onClick={onClose} className="p-2 hover:bg-norea-surfaceHover rounded" data-testid="btn-close-drawer">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-norea-primary" /></div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Column selector */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-norea-textMuted uppercase tracking-wider font-semibold">Colonne :</span>
              <select
                value={detail?.mail?.column_id || ""}
                onChange={(e) => moveToColumn(e.target.value)}
                className="text-xs border border-norea-border rounded px-2 py-1 bg-white outline-none focus:border-norea-primary"
                data-testid="drawer-column-select"
              >
                {columns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Dossier context */}
            {detail?.dossier && (
              <div className="bg-norea-primary/5 border border-norea-primary/20 rounded-lg p-3 text-xs">
                <div className="uppercase tracking-wider text-norea-primary font-semibold mb-1.5">Dossier lié</div>
                <div className="space-y-0.5 text-norea-text">
                  {(detail.dossier.extraction?.nom || detail.dossier.extraction?.prenom) && (
                    <div>Client : <b>{detail.dossier.extraction.prenom} {detail.dossier.extraction.nom}</b></div>
                  )}
                  {detail.dossier.partenaire && <div>Partenaire : <b>{detail.dossier.partenaire}</b></div>}
                  {detail.dossier.status && <div>Statut : <b>{detail.dossier.status}</b></div>}
                  {detail.dossier.pieces_manquantes?.length > 0 && (
                    <div>Pièces manquantes : <b>{detail.dossier.pieces_manquantes.join(", ")}</b></div>
                  )}
                  <a
                    href={`/dossiers/${detail.dossier.id}`}
                    className="text-norea-primary hover:underline inline-flex items-center gap-1 mt-1"
                    data-testid="link-dossier"
                  >
                    Ouvrir le dossier <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}

            {/* Original email content */}
            <div>
              <button
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center gap-2 text-xs uppercase tracking-wider text-norea-textMuted font-semibold mb-2"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Email reçu · {fmtDate(detail?.mail?.sent_at || mail.sent_at)}
              </button>
              {expanded && (
                <div className="bg-norea-bg border border-norea-border rounded p-3 text-sm text-norea-text whitespace-pre-wrap max-h-[280px] overflow-y-auto">
                  {detail?.mail?.body_text || "(corps vide)"}
                </div>
              )}
            </div>

            {/* AI proposed reply */}
            <div className="border border-norea-primary/30 bg-gradient-to-br from-norea-primary/5 to-transparent rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-norea-primary" />
                <span className="text-xs uppercase tracking-wider font-semibold text-norea-primary">Réponse proposée</span>
                {detail?.mail?.draft_status === "error" && (
                  <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                    Échec IA
                  </span>
                )}
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={12}
                className="w-full bg-white border border-norea-border rounded p-3 text-sm text-norea-text outline-none focus:border-norea-primary font-mono resize-y"
                placeholder="La réponse générée par l'IA apparaîtra ici…"
                data-testid="textarea-draft"
              />
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setShowReorient((v) => !v)}
                  className="text-xs px-2.5 py-1.5 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1.5"
                  data-testid="btn-reorient-ia"
                >
                  <RotateCcw className="w-3 h-3" /> Réorienter l'IA
                </button>
                <button
                  onClick={saveDraft}
                  disabled={saving}
                  className="text-xs px-2.5 py-1.5 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="btn-save-draft"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Sauvegarder
                </button>
                <button
                  onClick={() => regenerate()}
                  disabled={reorienting}
                  className="text-xs px-2.5 py-1.5 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="btn-regenerate"
                >
                  {reorienting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Regénérer
                </button>
              </div>
              {showReorient && (
                <div className="mt-3 p-3 bg-white border border-norea-primary/30 rounded">
                  <label className="text-[11px] uppercase tracking-wider text-norea-textMuted font-semibold">
                    Consigne à l'IA (ex : "plus court", "insiste sur la deadline")
                  </label>
                  <textarea
                    value={reorientPrompt}
                    onChange={(e) => setReorientPrompt(e.target.value)}
                    rows={2}
                    className="w-full mt-1.5 border border-norea-border rounded px-2 py-1.5 text-sm outline-none focus:border-norea-primary"
                    data-testid="textarea-reorient-prompt"
                  />
                  <button
                    onClick={regenerate}
                    disabled={reorienting || !reorientPrompt.trim()}
                    className="mt-2 text-xs px-3 py-1.5 rounded bg-norea-primary text-white hover:bg-norea-primary/90 disabled:opacity-50"
                    data-testid="btn-apply-reorient"
                  >
                    {reorienting ? "Génération..." : "Appliquer et régénérer"}
                  </button>
                </div>
              )}
            </div>

            {/* Send */}
            <div className="pt-2 border-t border-norea-border">
              <button
                onClick={send}
                disabled={sending || !draft.trim()}
                className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
                data-testid="btn-send-reply"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Valider &amp; envoyer
              </button>
              <div className="mt-2 text-center text-[11px] text-norea-textMuted">
                Envoyé vers <b>{mail.from_email}</b> · Signature auto-ajoutée par l'IA
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------- Settings modal -------------
function SettingsModal({ onClose, signature, onSaved }) {
  const [val, setVal] = useState(signature || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/reponses/settings", { signature: val });
      toast.success("Signature enregistrée");
      onSaved?.(val);
      onClose();
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="settings-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-norea-text">Signature de réponse</h3>
          <button onClick={onClose} className="p-1 hover:bg-norea-surfaceHover rounded"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-norea-textMuted mb-2">Cette signature sera automatiquement injectée en bas de chaque réponse générée par l'IA.</p>
        <textarea
          value={val}
          onChange={(e) => setVal(e.target.value)}
          rows={8}
          className="w-full border border-norea-border rounded p-3 text-sm outline-none focus:border-norea-primary font-mono"
          data-testid="textarea-signature"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-norea-border hover:bg-norea-surfaceHover">Annuler</button>
          <button onClick={save} disabled={saving} className="text-xs px-3 py-1.5 rounded bg-norea-primary text-white hover:bg-norea-primary/90 disabled:opacity-50" data-testid="btn-save-signature">
            {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------- Main page -------------
export default function BoiteReponsesPage() {
  const [columns, setColumns] = useState([]);
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMail, setOpenMail] = useState(null);
  const [signature, setSignature] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [polling, setPolling] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const [lastPoll, setLastPoll] = useState(null);
  const [pollSource, setPollSource] = useState("inbox");
  const [hiddenCount, setHiddenCount] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, mRes, sRes] = await Promise.all([
        api.get("/reponses/columns"),
        api.get("/reponses/mails?limit=300"),
        api.get("/reponses/settings"),
      ]);
      setColumns(cRes.data.items || []);
      setMails(mRes.data.items || []);
      setHiddenCount(mRes.data.hidden_count || 0);
      setSignature(sRes.data.signature || "");
      setLastPoll(new Date().toISOString());
    } catch (e) {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const byColumn = useMemo(() => {
    const map = {};
    columns.forEach((c) => (map[c.id] = []));
    mails.forEach((m) => {
      if (m.trashed) return;
      const cid = m.column_id;
      if (map[cid]) map[cid].push(m);
    });
    return map;
  }, [columns, mails]);

  const totalCount = useMemo(() => mails.filter((m) => !m.trashed).length, [mails]);

  const addColumn = async () => {
    const name = window.prompt("Nom de la nouvelle colonne ?");
    if (!name?.trim()) return;
    try {
      const r = await api.post("/reponses/columns", { name: name.trim() });
      setColumns((cs) => [...cs, r.data]);
      toast.success("Colonne créée");
    } catch {
      toast.error("Création impossible");
    }
  };

  const renameColumn = async (id, name) => {
    try {
      await api.patch(`/reponses/columns/${id}`, { name });
      setColumns((cs) => cs.map((c) => (c.id === id ? { ...c, name } : c)));
    } catch {
      toast.error("Renommage impossible");
    }
  };

  const deleteColumn = async (id, name) => {
    if (!window.confirm(`Supprimer la colonne "${name}" ? Les mails seront déplacés vers "À valider".`)) return;
    try {
      await api.delete(`/reponses/columns/${id}`);
      await loadAll();
      toast.success("Colonne supprimée");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const handleDragStart = (evt) => {
    setActiveDrag(evt.active.id);
  };

  const handleDragEnd = async (evt) => {
    const { active, over } = evt;
    setActiveDrag(null);
    if (!over) return;
    const activeType = active.data?.current?.type;
    if (activeType !== "mail") return;
    const overId = over.id.toString();
    if (!overId.startsWith("col-")) return;
    const targetColId = overId.slice(4);
    const mail = active.data.current.mail;
    if (mail.column_id === targetColId) return;
    // Optimistic
    setMails((ms) => ms.map((m) => (m.message_id === mail.message_id ? { ...m, column_id: targetColId } : m)));
    try {
      await api.patch(`/reponses/mails/${encodeURIComponent(mail.message_id)}/move`, { column_id: targetColId });
    } catch {
      toast.error("Déplacement impossible");
      loadAll();
    }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const r = await api.post(`/reponses/poll?source=${pollSource}&days=3&max_messages=50`, {}, { timeout: 240000 });
      if (r.data?.ok === false) {
        toast.error(`Import échoué : ${r.data.error || "erreur inconnue"}`);
      } else {
        toast.success(`${r.data.imported ?? 0} mails importés · ${r.data.matched ?? 0} rattachés · ${r.data.drafted ?? 0} brouillons IA`);
      }
      await loadAll();
    } catch (e) {
      // Timeout is common on first big import — the server keeps working. Just reload.
      toast.info("Import lancé en arrière-plan, actualisation dans un instant…");
      setTimeout(() => loadAll(), 2000);
    } finally {
      setPolling(false);
    }
  };

  const reanalyzeOld = async () => {
    if (!window.confirm("Régénérer les brouillons IA pour les 50 derniers mails sans brouillon ?")) return;
    setReanalyzing(true);
    try {
      const r = await api.post("/reponses/reanalyze", { limit: 50 });
      toast.success(`${r.data.regenerated} brouillons régénérés`);
      await loadAll();
    } catch {
      toast.error("Réanalyse échouée");
    } finally {
      setReanalyzing(false);
    }
  };

  const activeMail = useMemo(() => {
    if (!activeDrag || !activeDrag.startsWith("mail-")) return null;
    const id = activeDrag.slice(5);
    return mails.find((m) => m.message_id === id);
  }, [activeDrag, mails]);

  return (
    <AppLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-norea-border bg-white">
          <Breadcrumb items={[{ label: "Norea", to: "/" }, "Mails"]} />
          <div className="flex items-center gap-3 mb-1">
            <Inbox className="w-6 h-6 text-norea-primary" />
            <h1 className="text-xl font-semibold text-norea-text">Mails</h1>
            <span className="text-xs font-mono text-norea-textMuted bg-norea-bg px-2 py-0.5 rounded" data-testid="total-count">
              {totalCount} mails
            </span>
            <a href="/mails/ia" className="ml-2 text-xs font-medium px-2.5 py-1 rounded border border-norea-primary/40 text-norea-primary hover:bg-norea-primary/5" data-testid="link-inbox-ai">Tableau de bord IA des emails</a>
          </div>
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-norea-textMuted">
              Les emails reçus, rattachés à leur dossier, avec une réponse déjà pré-rédigée par l'IA.
              {lastPoll && <> Dernier relevé : <b>{timeAgo(lastPoll)}</b>.</>}
              {hiddenCount > 0 && (
                <> <a href="/configuration" className="underline hover:text-norea-primary" data-testid="noise-hidden-count">{hiddenCount} email(s) automatique(s) masqué(s)</a> par le filtre anti-bruit.</>
              )}
            </p>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-0.5 bg-norea-bg border border-norea-border rounded p-0.5" data-testid="poll-source-toggle">
                <button
                  onClick={() => setPollSource("label")}
                  className={`px-2 py-1 text-[11px] font-medium rounded transition ${pollSource === "label" ? "bg-white text-norea-text shadow-sm" : "text-norea-textMuted hover:text-norea-text"}`}
                  data-testid="poll-source-label"
                  title="Label configuré sur la connexion Gmail (ex : « 1/ Dossiers à traiter »)"
                >
                  Label Gmail
                </button>
                <button
                  onClick={() => setPollSource("inbox")}
                  className={`px-2 py-1 text-[11px] font-medium rounded transition ${pollSource === "inbox" ? "bg-white text-norea-text shadow-sm" : "text-norea-textMuted hover:text-norea-text"}`}
                  data-testid="poll-source-inbox"
                  title="Tout INBOX Gmail (mails du jour, non triés)"
                >
                  Boîte de réception
                </button>
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="text-xs px-2.5 py-1.5 rounded border border-norea-border hover:bg-norea-surfaceHover flex items-center gap-1.5"
                data-testid="btn-settings"
                title="Signature de réponse"
              >
                <Settings2 className="w-3.5 h-3.5" />
                Signature
              </button>
              <button
                onClick={reanalyzeOld}
                disabled={reanalyzing}
                className="text-xs px-2.5 py-1.5 rounded border border-norea-border hover:bg-norea-surfaceHover flex items-center gap-1.5 disabled:opacity-50"
                data-testid="btn-reanalyze"
              >
                {reanalyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                Réanalyser les anciens mails
              </button>
              <button
                onClick={pollNow}
                disabled={polling}
                className="text-xs px-3 py-1.5 rounded bg-norea-primary text-white hover:bg-norea-primary/90 flex items-center gap-1.5 disabled:opacity-50"
                data-testid="btn-poll"
              >
                {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Actualiser
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 pt-4 bg-norea-bg">
          <AiInsight scope="reponses" title="Synthèse IA des emails à traiter" />
        </div>

        {/* Kanban */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden bg-norea-bg">
          {loading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-norea-primary" />
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setActiveDrag(null)}
            >
              <div className="flex gap-4 p-6 min-h-full">
                {columns.map((col) => (
                  <Column
                    key={col.id}
                    col={col}
                    mails={byColumn[col.id] || []}
                    onOpenMail={setOpenMail}
                    onRename={renameColumn}
                    onDelete={deleteColumn}
                    activeDragId={activeDrag}
                    allColumns={columns}
                  />
                ))}
                {/* Add column */}
                <button
                  onClick={addColumn}
                  className="w-[240px] min-w-[240px] flex items-center justify-center gap-2 border-2 border-dashed border-norea-border rounded-lg text-sm text-norea-textMuted hover:border-norea-primary hover:text-norea-primary transition-colors self-start py-6"
                  data-testid="btn-add-column"
                >
                  <Plus className="w-4 h-4" /> Ajouter une colonne
                </button>
              </div>
              <DragOverlay>
                {activeMail && <MailCard mail={activeMail} onClick={() => {}} isDragging={false} />}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </div>

      {openMail && (
        <MailDrawer
          mail={openMail}
          columns={columns}
          signature={signature}
          onClose={() => setOpenMail(null)}
          onSent={() => loadAll()}
          onMoved={(mid, cid) => setMails((ms) => ms.map((m) => (m.message_id === mid ? { ...m, column_id: cid } : m)))}
        />
      )}

      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          signature={signature}
          onSaved={(v) => setSignature(v)}
        />
      )}
    </AppLayout>
  );
}
